import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { GOOGLE_MAPS_URL } from '../seo/hotelFacts';

const API = process.env.REACT_APP_BACKEND_URL;
let cache = null;

const L = {
  en: { title: 'Google reviews', sub: 'Live from our Google Business Profile', reviews: 'reviews on Google', all: 'See all reviews on Google', write: 'Write a review', more: 'Read more', less: 'Show less' },
  me: { title: 'Google recenzije', sub: 'Uživo sa našeg Google poslovnog profila', reviews: 'recenzija na Googleu', all: 'Sve recenzije na Googleu', write: 'Napišite recenziju', more: 'Pročitaj više', less: 'Prikaži manje' },
  sq: { title: 'Vlerësime në Google', sub: 'Drejtpërdrejt nga profili ynë në Google', reviews: 'vlerësime në Google', all: 'Shiko të gjitha në Google', write: 'Shkruaj një vlerësim', more: 'Lexo më shumë', less: 'Më pak' },
  de: { title: 'Google-Bewertungen', sub: 'Live aus unserem Google-Unternehmensprofil', reviews: 'Bewertungen auf Google', all: 'Alle Bewertungen auf Google', write: 'Bewertung schreiben', more: 'Mehr lesen', less: 'Weniger' },
  pl: { title: 'Opinie Google', sub: 'Na żywo z naszego Profilu Firmy w Google', reviews: 'opinii w Google', all: 'Wszystkie opinie w Google', write: 'Napisz opinię', more: 'Czytaj więcej', less: 'Mniej' },
  ru: { title: 'Отзывы Google', sub: 'Напрямую из нашего Google Бизнес-профиля', reviews: 'отзывов в Google', all: 'Все отзывы в Google', write: 'Написать отзыв', more: 'Читать далее', less: 'Свернуть' },
  hu: { title: 'Google-értékelések', sub: 'Élőben a Google cégprofilunkból', reviews: 'értékelés a Google-on', all: 'Minden értékelés a Google-on', write: 'Írjon értékelést', more: 'Tovább', less: 'Kevesebb' },
  ro: { title: 'Recenzii Google', sub: 'Live din Profilul nostru de afaceri Google', reviews: 'recenzii pe Google', all: 'Toate recenziile pe Google', write: 'Scrie o recenzie', more: 'Citește mai mult', less: 'Mai puțin' },
};

const Stars = ({ n = 0, size = 'w-4 h-4' }) => (
  <span className="inline-flex gap-0.5" aria-label={`${n} / 5`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${size} ${i <= Math.round(n) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />)}
  </span>
);

const GIcon = () => (
  <svg viewBox="0 0 48 48" className="w-7 h-7" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.5 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.5 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.3 6.9-17.7z"/><path fill="#FBBC05" d="M10.5 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.9-6.1C.9 16.6 0 20.2 0 24s.9 7.4 2.6 10.8l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.7-6c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.6-4-13.5-9.7l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
);

const ReviewCard = ({ r, labels }) => {
  const [full, setFull] = useState(false);
  const long = (r.snippet || '').length > 220;
  return (
    <article className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col" data-testid="google-review-card">
      <div className="flex items-center gap-3">
        {r.thumbnail ? <img src={r.thumbnail} alt="" className="w-10 h-10 rounded-full object-cover" loading="lazy" referrerPolicy="no-referrer" /> : <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-semibold">{(r.name || '?').charAt(0)}</div>}
        <div className="min-w-0"><p className="font-medium text-slate-900 truncate">{r.name}</p><p className="text-xs text-slate-500">{r.date}</p></div>
        <Stars n={r.rating} size="w-3.5 h-3.5" />
      </div>
      <p className={`mt-4 text-sm text-slate-700 leading-relaxed ${full ? '' : 'line-clamp-5'}`}>{r.snippet}</p>
      {long && <button type="button" onClick={() => setFull(!full)} className="mt-2 text-xs text-amber-800 hover:underline self-start">{full ? labels.less : labels.more}</button>}
      {r.link && <a href={r.link} target="_blank" rel="noopener noreferrer" className="mt-auto pt-4 text-xs text-slate-400 hover:text-slate-600 inline-flex items-center gap-1">Google <ExternalLink className="w-3 h-3" /></a>}
    </article>
  );
};

// Live Google Business Profile reviews (SerpApi via backend, cached 24 h). Renders nothing until a SerpApi key is configured.
export default function GoogleReviews() {
  const { language } = useLanguage();
  const labels = L[language] || L.en;
  const [data, setData] = useState(cache);
  useEffect(() => {
    if (cache) return undefined;
    let alive = true;
    axios.get(`${API}/api/public/google-reviews`).then((r) => { cache = r.data; if (alive) setData(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!data?.configured || !data.reviews?.length) return null;
  const placeUrl = data.place_url || GOOGLE_MAPS_URL;

  return (
    <section className="bg-[#f5f1ea] border-b border-amber-100" data-testid="google-reviews">
      <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-amber-800"><GIcon />{labels.title}</p>
            <div className="mt-3 flex items-center gap-4">
              <span className="text-5xl font-bold text-slate-900" data-testid="google-rating">{Number(data.rating).toFixed(1)}</span>
              <div><Stars n={data.rating} size="w-5 h-5" /><p className="text-sm text-slate-600 mt-1">{data.reviews_count} {labels.reviews}</p></div>
            </div>
            <p className="text-sm text-slate-500 mt-2">{labels.sub}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={placeUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-slate-300 hover:border-amber-500 text-slate-700 hover:text-amber-700 text-sm px-5 py-2.5 rounded-full transition-colors" data-testid="google-reviews-all">{labels.all}<ExternalLink className="w-3.5 h-3.5" /></a>
            {data.write_url && <a href={data.write_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white text-sm px-5 py-2.5 rounded-full transition-colors" data-testid="google-reviews-write">{labels.write}</a>}
          </div>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.reviews.slice(0, 9).map((r, i) => <ReviewCard key={r.link || `${r.name}-${i}`} r={r} labels={labels} />)}
        </div>
      </div>
    </section>
  );
}
